/**
 * Billing types.
 *
 * Shapes only. Every number that fills them lives in `config.ts`.
 */

// ── Tiers ────────────────────────────────────────────────────────────────────

export const SubscriptionTier = {
  FREE: "free",
  PRO: "pro",
  TEAM: "team",
  ENTERPRISE: "enterprise",
} as const;

export type SubscriptionTier =
  (typeof SubscriptionTier)[keyof typeof SubscriptionTier];

/** Display order: cheapest first. Used by both pricing surfaces. */
export const TIER_ORDER: readonly SubscriptionTier[] = [
  SubscriptionTier.FREE,
  SubscriptionTier.PRO,
  SubscriptionTier.TEAM,
  SubscriptionTier.ENTERPRISE,
] as const;

// ── Limits ───────────────────────────────────────────────────────────────────

/**
 * A ceiling. `null` means unlimited.
 *
 * Deliberately not `Infinity`: `Infinity` silently satisfies every `<`
 * comparison, so an unlimited tier slips through code that forgot to handle it
 * and looks like it worked. `null` makes the unlimited branch a compile-time
 * obligation, and survives JSON and DynamoDB round-trips unchanged.
 */
export type Limit = number | null;

/** The value of {@link Limit} meaning "no ceiling". */
export const UNLIMITED: Limit = null;

export function isUnlimited(limit: Limit): limit is null {
  return limit === null;
}

export interface PlanLimits {
  /**
   * Monthly AI credits, before any streak bonus. 1 credit = $0.001 of actual
   * provider cost (see `CREDIT_MICRO_USD`); replaces the old flat
   * `monthlyGenerations` count so a plan's allowance tracks what work really
   * costs rather than one number per output regardless of size.
   */
  readonly monthlyCredits: Limit;
  /**
   * @deprecated `monthlyCredits` is the real allowance now; this is
   * `Math.floor(monthlyCredits / STANDARD_POST_CREDITS)`, kept — always
   * populated, never optional — so packages that have not migrated off the
   * old unit (the `GET /usage` deprecation window, and any catalogue
   * consumer that reads it directly) keep compiling and showing a correct,
   * if approximate, figure.
   */
  readonly monthlyGenerations: Limit;
  /** Personas the account may keep. */
  readonly personas: Limit;
  /**
   * X posts guaranteed each month, counted as if every post contained a link.
   *
   * Billed on the ceiling, not the floor: the plan's X budget is this many
   * posts at the with-URL price, and each post deducts what it actually costs.
   * A user who posts without links gets more than this; nobody gets fewer.
   */
  readonly monthlyXPosts: Limit;
  /**
   * Suggestions (a highlight rewrite, an Ask…, a feedback-panel Fix, or an
   * on-demand "Suggest improvements" run) free each month, before credits
   * take over. Unlike the credit meter, there is no grace allowance here: a
   * suggestion past this ceiling is never refused, it is simply priced in
   * credits like any other AI call (see `SUGGESTION_CREDITS`).
   */
  readonly monthlySuggestions: Limit;
  /**
   * Studio Chat messages free each month, Ask and Edit on one shared meter.
   * Like suggestions there is no grace: past it a message is priced in
   * credits (`CHAT_ASK_CREDITS`, `CHAT_EDIT_CREDITS`), and only when those are
   * gone too is it refused.
   */
  readonly monthlyChatMessages: Limit;
  /**
   * How many days a Timeline entry is kept before it is pruned. `null` keeps
   * entries forever. Named entries and the entry that went out at publish are
   * exempt on every plan.
   */
  readonly historyRetentionDays: Limit;
  /** Entries one draft may have a name on at once. Renaming a named entry does not count again. */
  readonly namedVersionsPerDraft: Limit;
  /** Custom voices the account may keep active. Past it after a downgrade, the extras are locked, not deleted. */
  readonly voices: Limit;
  /** Rooms the account may own. A room is owned by the account holding the paid plan. */
  readonly rooms: Limit;
  /** Members in each room that account owns. */
  readonly roomMembers: Limit;
  /** Tasks that may be active at once. A paused task does not count. */
  readonly activeTasks: Limit;
  /** The shortest repeat a task schedule may use on this plan. */
  readonly shortestTaskRepeat: "daily" | "weekly";
  /**
   * Workflows the account may keep. A scheduled workflow also takes one of the
   * active-task slots above; a manual or event-started one does not.
   */
  readonly workflows: Limit;
}

// ── What we charge ───────────────────────────────────────────────────────────

export interface PlanFeature {
  readonly label: string;
  readonly included: boolean;
}

export interface Plan {
  readonly key: SubscriptionTier;
  readonly name: string;
  /** USD per month on monthly billing. `null` means "contact sales". */
  readonly monthlyPrice: number | null;
  /** USD per month when billed annually. `null` means "contact sales". */
  readonly annualPrice: number | null;
  readonly tagline: string;
  readonly badge?: string;
  readonly highlighted?: boolean;
  readonly cta: string;
  readonly limits: PlanLimits;
  /**
   * Marketing bullets that are NOT derivable from limits (platforms, support
   * tier, analytics). Anything numeric the backend enforces belongs in
   * `limits` and is rendered from it.
   */
  readonly extraFeatures: readonly PlanFeature[];
}

// ── How limits are enforced ──────────────────────────────────────────────────

export interface EnforcementPolicy {
  /** Allowance past a plan's limit before requests are refused. */
  readonly grace: {
    /** The allowance as a share of the limit. */
    readonly ratio: number;
    /**
     * Floor on the generation allowance, in generations.
     *
     * @deprecated Superseded by `minimumCredits` now that the AI meter is
     * credits, not generations. Kept only so `generationBounds()` — still
     * used by the in-app meter to render the deprecated `GET /usage` fields
     * for one release — has a floor to apply.
     */
    readonly minimumGenerations: number;
    /** Floor on the credit allowance, in credits. */
    readonly minimumCredits: number;
    /** Floor on the X allowance, in link-priced posts. */
    readonly minimumXPosts: number;
  };
  /**
   * While a user is inside their grace allowance, interactive requests are
   * spaced at least this far apart. Scheduled jobs are not throttled.
   */
  readonly throttle: {
    readonly minIntervalSeconds: number;
  };
}

// ── What it costs us ─────────────────────────────────────────────────────────

/**
 * Where a price came from, and when someone last checked it against that
 * source.
 *
 * `verifiedOn: null` marks a figure nobody has re-checked — a carried-over
 * number. It is allowed, but it is visible, which is the point: the Gemini
 * rates this replaced were preview-era prices that went stale unnoticed and
 * under-counted real cost roughly fifteenfold.
 */
export interface RateProvenance {
  readonly source: string | null;
  /** ISO date, e.g. "2026-09-14". */
  readonly verifiedOn: string | null;
}

export type AiProvider = "gemini" | "openai" | "anthropic" | "vertex-maas";

export interface ModelRate extends RateProvenance {
  readonly provider: AiProvider;
  /** USD per one million input (prompt) tokens. */
  readonly inputUsdPerMillion: number;
  /** USD per one million output tokens. */
  readonly outputUsdPerMillion: number;
  /**
   * Whether the provider bills reasoning ("thinking") tokens at the output
   * rate. Gemini does, and reports them separately from visible output — so
   * pricing only the visible output silently drops them.
   */
  readonly thinkingBilledAsOutput: boolean;
  /**
   * A price change the provider has already published.
   *
   * Cost is computed with the rate in force when the call happened, so a
   * scheduled increase does not quietly halve what we record on the day it
   * lands. Gemini 3 Flash is promotional until 2027 and then doubles; without
   * this, every figure after that date would be understated until someone
   * noticed and edited the rate by hand.
   */
  readonly scheduledChange?: {
    /** ISO date the new prices take effect, UTC. */
    readonly from: string;
    readonly inputUsdPerMillion: number;
    readonly outputUsdPerMillion: number;
  };
}

/**
 * A per-call price in USD, or `null` when the provider does not publish one.
 *
 * `null` is recorded as zero cost but flagged as unpriced, never assumed to be
 * free: an operation we cannot price is a gap in the numbers, and it must show
 * up as one.
 */
export type CallPriceUsd = number | null;

export interface XRates extends RateProvenance {
  /** How X bills this app. Only pay-per-use has per-call prices. */
  readonly billing: "pay-per-use";
  readonly postCreateUsd: CallPriceUsd;
  /** X charges considerably more for a post that contains a URL. */
  readonly postCreateWithUrlUsd: CallPriceUsd;
  readonly userReadUsd: CallPriceUsd;
  /** Reading one post by id. Cheaper than a user read, dearer than nothing. */
  readonly postReadUsd: CallPriceUsd;
  /**
   * Deleting a post. `0` because X publishes no charge for it, which is not
   * the same as `null`: a null price is one we do not know, and calls priced
   * that way are counted as unpriced so a reader knows the total is a floor.
   */
  readonly postDeleteUsd: CallPriceUsd;
  readonly mediaUploadUsd: CallPriceUsd;
}

/**
 * Cloudflare Browser Rendering, billed by time: a free daily allowance, then
 * per browser-hour. Used only for the rendered-fetch step of web extraction
 * (a real browser loading a page we couldn't read directly) — imports are
 * never charged to the user (see LAYERS.md), so this rate feeds cost
 * measurement only, never a quota.
 */
export interface CloudflareRates extends RateProvenance {
  readonly billing: "pay-per-use";
  readonly browserHourUsd: CallPriceUsd;
}

/** What an image model charges per picture, whatever its size. */
export interface ImageModelRate extends RateProvenance {
  readonly provider: "vertex-imagen" | "vertex-gemini";
  readonly usdPerImage: number;
}

/**
 * Speech-to-text, billed per minute of audio. Used by dictation's server
 * fallback for browsers with no speech recognition of their own.
 */
export interface TranscriptionModelRate extends RateProvenance {
  readonly provider: "openai";
  readonly usdPerMinute: CallPriceUsd;
}

export interface Rates {
  readonly ai: {
    readonly models: Readonly<Record<string, ModelRate>>;
  };
  readonly images: {
    readonly models: Readonly<Record<string, ImageModelRate>>;
  };
  readonly transcription: {
    readonly models: Readonly<Record<string, TranscriptionModelRate>>;
  };
  readonly x: XRates;
  readonly cloudflare: CloudflareRates;
}

/**
 * Integer micro-dollars (1 USD = 1,000,000). Costs are summed with atomic
 * DynamoDB ADD operations, and integers add exactly where floats drift.
 */
export type MicroUsd = number;
